import { Fragment, h } from '@stencil/core';
import type { ObservableMap } from '@stencil/store';

import type { MatchStoreState } from '../../../services/interfaces';
import { HomeAwayTeam, MatchUnitSize } from '../../../services/interfaces';

import {
  LiveIndicator,
  MatchAggResult,
  MatchCta,
  MatchFirstLegResult,
  MatchInfo,
  MatchPenaltyScore,
  MatchPostStatus,
  MatchRelatedBrackets,
  MatchScore,
  MatchTeam,
} from './components';
import { isLiveMatchPenalties, matchPenaltyScore } from './helpers';
import { isFinishedMatch, isSizePlus, printCta, setCssVariant } from './helpers/match-unit.helpers';
import { MatchUnitVerticalMiddleLeft } from './match-unit-vertical-middle-left';
import { MatchUnitVerticalMiddleRight } from './match-unit-vertical-middle-right';

type Props = {
  state: ObservableMap<MatchStoreState>['state'];
  onCtaClick: (e: MouseEvent) => void;
};

const MatchUnitVertical = ({ state, onCtaClick }: Props, children: HTMLDivElement) => {
  const size = state.size;
  const cssVariant = setCssVariant(size);
  const match = state.matchData;

  const isLivePenalties = isLiveMatchPenalties(match.status, match.phase);
  const finishedMatch = isFinishedMatch(match.status);
  const havePenaltiesScore =
    isLivePenalties ||
    (finishedMatch && (!!matchPenaltyScore(match, HomeAwayTeam.HOME) || !!matchPenaltyScore(match, HomeAwayTeam.AWAY)));
  const showMatchUnitSmallSize = size === MatchUnitSize.S || isSizePlus(size);
  const showHighlightUrl = state.showHighlightUrl;

  const hideCta = state.hideCta;
  const isCtaSlotEmpty = state.isCtaSlotEmpty;
  const isPlusSize = isSizePlus(size);
  const showPlusCta = !hideCta && isCtaSlotEmpty && printCta(match);
  const shownInBrackets = state.shownInBrackets;

  return (
    <Fragment>
      <div class={{ 'mu__vertical--top': true, ...cssVariant }} part="top">
        {!isPlusSize && <MatchUnitVerticalMiddleLeft state={state} />}
        <div class="center" part="center">
          {size === MatchUnitSize.L && (
            <Fragment>
              <MatchFirstLegResult state={state} />
              <MatchAggResult state={state} />
              <MatchInfo state={state} />
            </Fragment>
          )}
        </div>

        {!isPlusSize && (
          <Fragment>
            <MatchUnitVerticalMiddleRight state={state}></MatchUnitVerticalMiddleRight>
            {/* CTA slot */}
            {children}
            {/* End CTA slot */}
          </Fragment>
        )}
      </div>
      <div class={{ 'mu__vertical--middle': true, ...cssVariant }} part="middle">
        {showMatchUnitSmallSize &&
          (shownInBrackets ? (
            <MatchRelatedBrackets state={state} />
          ) : (
            <Fragment>
              <MatchFirstLegResult state={state} />
              <MatchAggResult state={state} />
              <MatchInfo state={state} />
            </Fragment>
          ))}
        {/* When the MU is displayed in brackets, the post status is displayed as part of the MatchRelatedBrackets component */}
        {!shownInBrackets && <MatchPostStatus state={state} />}
      </div>
      <div
        class={{ 'mu__vertical--core': true, 'have-penalty-score': havePenaltiesScore, ...cssVariant }}
        part="vertical-core"
      >
        <div class={{ 'mu__vertical--teams': true, ...cssVariant }} part="teams">
          <MatchTeam state={state} />
          <MatchTeam state={state} isAwayTeam={true} />
        </div>
        <MatchPenaltyScore state={state} />
        <MatchScore state={state} />
        {isPlusSize && (
          <div class={{ 'mu__vertical-core-time-cta': true, ...cssVariant }}>
            {!showHighlightUrl && (
              <Fragment>
                <MatchUnitVerticalMiddleLeft state={state} />
                <MatchUnitVerticalMiddleRight state={state} />

                {/* Custom live indicator for plus sizes */}
                {showPlusCta && <MatchCta state={state} onCtaClick={onCtaClick} />}

                <LiveIndicator state={state} onCtaClick={onCtaClick} />
              </Fragment>
            )}
            {/* CTA slot */}
            {children}
            {/* End CTA slot */}
          </div>
        )}
      </div>
    </Fragment>
  );
};

export { MatchUnitVertical };
